// "Best 3D printing services for {city}" guides, rendered by
// PrintShopsGuide.js like /guides/best-3d-print-shops-nyc/.
//
// PrintAWorld has no studio in these cities. The owner's call (2026-11-03):
// frame each page as companies that serve the city, and say plainly that we
// are a New York production studio that ships there. This is an approved
// exception to the "no distance framing" city-page rule in .seo/truth.md;
// the city service pages themselves don't change. Owner facts: many clients
// in SF, LA and Houston, quick replies, projects done in a matter of days,
// and no same-day or next-day outside NYC because there's no local location.
//
// Every local shop's facts come from its own website, read on `checkedDate`;
// the URLs are in each city's `sources`. Their pages are data: describe what
// they say, never guess at the rest. Recheck every shop before changing
// `checkedDate`, and move `year` each January.
const locationData = require('../../lib/locations')

const pricing = {
  printFrom: 30,
  modelingFrom: 250,
  trophyFrom: 250,
}

const year = 2026
const checked = {
  publishedDate: '2026-11-03',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  checkedDate: '2026-11-03',
  checkedDateLabel: 'November 3, 2026',
  correctionsEmail: 'contact@prtwd.com',
}

const studios = locationData.locations.map((location) => location.address)

const photoRoot = 'https://images.ctfassets.net/dovsnf6qrcem'
const heroPhotos = {
  carFrame: {
    url: `${photoRoot}/13qMXcxU3RPgNQPCqSDTSW/a263d44046ca44f4700e9c644630cf3f/Audi_Car_Frame_3d_Print.jpg`,
    width: 2880,
    height: 1800,
    alt: 'A large 3D printed scale model of a car body frame',
    caption: 'A large-format scale model of a car body frame',
  },
  crab: {
    url: `${photoRoot}/4xgrmZKw1AadDMNJS9EDU1/e325f2b432f1994a87f4b6eb92f24a3c/LAL_4491.jpg`,
    width: 7360,
    height: 4912,
    alt: 'A clear resin 3D printed crab on a black background',
    caption: 'Printed in clear resin to show every joint of the shell',
  },
  chrome: {
    url: `${photoRoot}/2qVhcDIOuVkHgiLUjtCg3a/0f7ca7f3da72305256fc083aac3af12c/LAL_6513.jpg`,
    width: 3000,
    height: 1974,
    alt: 'Two 3D printed guitar-player figures, one raw white print and one chrome plated',
    caption: 'The same print straight off the printer and after chrome plating',
  },
}

const askList = [
  'Do you print in-house, or send the job to a partner?',
  'Which process and material do you suggest for how the piece will be used?',
  'What is the largest single piece you can print, and how do you join sections?',
  'Is sanding, priming, painting or plating included, or quoted separately?',
  'Is there a minimum order or a setup fee?',
  'Does the schedule start when I approve the file, or when I pay?',
  'Can I pick it up, or how long does shipping take to me?',
  'Can you build the file from a sketch, photo or scan, and do I get the file?',
]

// PrintAWorld's row, facts and answers, the same in every city.
const ourShop = (city) => ({
  id: 'printaworld',
  name: 'PrintAWorld',
  url: 'https://prtwd.com/',
  ours: true,
  where: `New York studios in Glendale, Queens and Brooklyn. No location in ${city}: we work with ${city} clients remotely and ship to them, and to all 50 states.`,
  prints:
    'In-house FDM print farm, SLA resin, large-format and stainless steel (SLM) metal. Large production orders, architectural and scale models, and builds of 6 ft and more. Sanding, painting and metal plating.',
  price: `A person reviews your file, sketch or photo and sends a written quote. From about $${pricing.printFrom}, one print minimum, no setup fee.`,
  goodFor: `Brands, agencies, architects and production work in ${city} that need one team to own the result: production runs, architectural and scale models, very large builds, painted prints, metal parts and trophies. Projects done in a matter of days.`,
  link: ['Architectural models', '/services/architectural-3d-printing/'],
})

const ourFacts = (city) => [
  [
    'Price',
    `From about $${pricing.printFrom}, one print minimum, no setup fee`,
  ],
  ['Studios', `${studios.join('; ')}. No location in ${city}`],
  [
    `Serving ${city}`,
    `Many clients in ${city}; we reply quickly and get projects done in a matter of days`,
  ],
  [
    'Processes',
    'FDM print farm, SLA resin, large-format and stainless steel (SLM) metal',
  ],
  ['Orders', 'One print to large production orders'],
  ['Largest builds', '6 ft and more, printed in sections and assembled'],
  ['Finishing', 'Sanding, priming, painting and metal plating, quoted per job'],
  ['Design', `3D modeling and 3D scanning from $${pricing.modelingFrom}`],
  [
    'Delivery',
    `Ships to ${city} and all 50 states. No pickup, same-day or next-day in ${city}`,
  ],
  [
    'Best for',
    'Businesses, brands, agencies, architects, creative and production work',
  ],
  [
    'Look elsewhere for',
    `Pickup or a print today: a local ${city} shop. Cheap hobby prints or parts to resell: a marketplace or hobbyist`,
  ],
]

const sharedFaqs = (city) => [
  {
    question: `Can PrintAWorld 3D print for ${city} without a local studio?`,
    answer: `Yes. PrintAWorld is a New York production studio with many clients in ${city}. Send a file, sketch or photo; a person replies quickly with a written quote, we print and finish the piece in our own studios, and ship it to you, usually within a matter of days. We have no location in ${city}, so there’s no pickup, and same-day or next-day isn’t possible there.`,
  },
  {
    question: 'Who is PrintAWorld a good fit for?',
    answer:
      'Businesses, brands, agencies, architects, and creative and production teams that need one team to own the result, from one print to a large production order, including architectural and scale models for presentations, competitions and displays. It is not the cheapest option for hobby prints or for parts you plan to resell: a marketplace like Craftcloud or a hobbyist will cost less.',
  },
  {
    question: 'How do I correct a fact on this page?',
    answer: `Email ${checked.correctionsEmail} with the fact and a link to the page on your website that shows it. We’ll recheck it and update the page and its date.`,
  },
]

// One city page from its local shops. `local` holds what only that city
// has: its shops and sources, fit cards, honest-fit rows and FAQ answers.
const cityGuide = ({ city, slug, cityPage, hero, local }) => {
  const guide = {
    path: `/guides/best-3d-printing-services-${slug}/`,
    city,
    year,
    title: `Best 3D Printing Services for ${city} (${year})`,
    heading: `Best 3D printing services for ${city} (${year})`,
    cardTitle: `Best 3D printing services for ${city} (${year})`,
    cityPage,
    ...checked,
  }
  const shops = [ourShop(city), ...local.shops]
  const others = local.shops.map((shop) => shop.name)
  return {
    guide,
    metaTitle: `Best 3D Printing Services for ${city} (${year})`,
    description: `${shops.length} 3D printing services for ${city} compared from their own websites: local shops and a New York studio that ships there. Checked ${checked.checkedDateLabel}.`,
    answer: local.answer,
    pricing,
    shops,
    sources: local.sources,
    jobs: local.jobs,
    otherShopFits: [
      ...local.otherShopFits,
      [
        'A marketplace or a hobbyist',
        'You want the cheapest print of a hobby file, or parts to resell. A marketplace like Craftcloud or a hobbyist will cost less than we do.',
      ],
    ],
    askList,
    ourFacts: ourFacts(city),
    faqs: [...local.faqs, ...sharedFaqs(city)],
    photos: { hero: heroPhotos[hero] },
    copy: {
      crumb: `Best 3D printing services for ${city}`,
      eyebrow: `3D printing services for ${city}`,
      noun: ['company', 'companies'],
      questionsLink: `${city} 3D printing questions`,
      tableLabel: `3D printing services for ${city} compared`,
      ourPath: cityPage,
      about: `3D printing services for ${city}`,
      h2: {
        compared: `3D printing services for ${city}, side by side`,
        fit: `Which 3D printing service in ${city} fits your job?`,
        band: `How PrintAWorld’s 3D printing service works for ${city}`,
        versus: `A local ${city} 3D printing shop or PrintAWorld?`,
        others: `When a local ${city} 3D printing shop is the better choice`,
        ask: `What to ask a 3D printing service in ${city}`,
        questions: `Questions about 3D printing services in ${city}`,
        sources: `Sources for these ${city} 3D printing services`,
      },
      pickedTitle: `How we picked these 3D printing services for ${city}`,
      picked: `This page lists companies that serve ${city}, not only shops located there. ${others.join(', ').replace(/, ([^,]*)$/, ' and $1')} have a shop ${local.area || `in or near ${city}`}, print for the public and have a current website. PrintAWorld is a New York studio that ships to ${city}. Shops that have closed, online marketplaces without a local shop, and makerspaces where you print yourself are left out. “Best” here means best for a kind of job: no company is best at everything.`,
      band: [
        `We are a New York production studio for businesses, brands, agencies and creative teams, and we have many clients in ${city}. Everything is made in-house in our two New York studios: an FDM print farm for large production orders, SLA resin for detail and architectural models, large-format printers for builds of 6 ft and more, and stainless steel SLM for metal. The same team sands, paints and plates, and a person reviews every file and owns the result.`,
        `We reply quickly and get projects done in a matter of days, then ship to ${city}. We have no location in ${city}, so there’s no pickup, and same-day or next-day isn’t possible there. If you need to collect a part today, a local shop on this list fits better.`,
      ],
      serviceLink: [`Our 3D printing service for ${city}`, cityPage],
      askLinkLabel: `3D printing for ${city}`,
      versus: {
        id: 'local-or-printaworld',
        paragraphs: local.versus,
        link: [`3D printing for ${city}`, cityPage],
      },
      related: [
        [`3D printing for ${city}`, cityPage],
        ['3D printing service', '/services/3d-printing/'],
        ['Architectural 3D printing', '/services/architectural-3d-printing/'],
      ],
    },
  }
}

// Los Angeles shops, read 2026-11-03. West LA 3D Printing was left out: its
// footer says 2024 and its quotes page reports hosting problems.
const losAngeles = cityGuide({
  city: 'Los Angeles',
  slug: 'los-angeles',
  cityPage: '/3d-printing-service-los-angeles/',
  hero: 'carFrame',
  local: {
    answer:
      'The best 3D printing service for Los Angeles depends on the job. For a part today, eCadCam in Glendale. For film and TV props, Hollywood 3D Printing, There You Have It or Gentle Giant Studios. For brands, agencies, architects and production runs that need one team to own the result, PrintAWorld ships from New York in days.',
    shops: [
      {
        id: 'ecadcam',
        name: 'eCadCam',
        url: 'https://ecadcam.com/',
        where:
          'Glendale (4212 San Fernando Road). Free pickup in Glendale, or UPS/FedEx nationwide.',
        prints:
          'FDM, SLA, SLS and PolyJet, plus castable wax; metal (DMLS) quoted by an engineer. Larger parts split and bonded. Sanding, priming and painting.',
        price:
          'Instant online quote, then an engineer review, or a free-quote form. No minimum order.',
        goodFor:
          'Prototypes, jewelry and custom parts. Same day for many FDM and wax parts uploaded before noon; rush in 1–3 business days.',
      },
      {
        id: 'hollywood-3d-printing',
        name: 'Hollywood 3D Printing',
        url: 'https://www.hollywood3dprinting.com/',
        where:
          'Fabrication studio in Van Nuys (16022 Arminta Street); office in West Hollywood. Pickup, or same-day delivery across the city.',
        prints:
          'SLA, FDM and SLS nylon; pieces up to 10–15 ft with assembly. Pantone-matched painting, chrome and gold electroplating, CNC foam and molds.',
        price:
          'Instant quotes with an account, or a quote request answered by a project engineer, typically within 15–30 minutes.',
        goodFor:
          'Brand activations, events, trade show displays, film and TV, and corporate awards. Most fabrication projects take 2 to 6 weeks.',
      },
      {
        id: 'there-you-have-it',
        name: 'There You Have It',
        url: 'https://www.thereyouhaveit3d.com/',
        where:
          'Burbank (3223 Burton Avenue), by appointment only. Local pickup or shipping worldwide.',
        prints:
          'Industrial SLA up to 1000 × 1000 × 580 mm, MJF nylon and FDM. Also scanning, molds, casting, CNC, paint work and metal plating.',
        price:
          'Quote request form, contact form or phone; quick-turnaround jobs by phone.',
        goodFor:
          'Film and TV props: it calls itself one of Hollywood’s leading prop manufacturing and technical service bureaus. Open to all customers.',
      },
      {
        id: 'gentle-giant-studios',
        name: 'Gentle Giant Studios',
        url: 'https://www.gentlegiantstudios.com/',
        where:
          'Burbank (7511 North San Fernando Road). Local pickups; works worldwide.',
        prints:
          'SLS, SLA, MJP, full-color and DLP; full-size prop weapons and suits of armor printed in a single piece. Post-processing and professional painting.',
        price:
          'A secure file-upload project form for businesses, or a contact form and phone. No instant quote.',
        goodFor:
          'Film studios, theme parks and product designers: film maquettes, collectibles and game prototypes.',
      },
    ],
    sources: [
      {
        shop: 'eCadCam',
        pages: [
          'https://ecadcam.com/',
          'https://ecadcam.com/3d-printing/',
          'https://ecadcam.com/instant-quote/',
          'https://ecadcam.com/request-a-quote/',
        ],
      },
      {
        shop: 'Hollywood 3D Printing',
        pages: [
          'https://www.hollywood3dprinting.com/',
          'https://www.hollywood3dprinting.com/services/3d-printing-los-angeles',
          'https://www.hollywood3dprinting.com/get-a-quote',
          'https://www.hollywood3dprinting.com/blog/custom-fabrication-lead-times',
        ],
      },
      {
        shop: 'There You Have It',
        pages: [
          'https://www.thereyouhaveit3d.com/',
          'https://www.thereyouhaveit3d.com/3d-printing',
          'https://www.thereyouhaveit3d.com/fabrication',
          'https://www.thereyouhaveit3d.com/contact',
        ],
      },
      {
        shop: 'Gentle Giant Studios',
        pages: [
          'https://www.gentlegiantstudios.com/services/3d-printing-in-los-angeles/',
          'https://www.gentlegiantstudios.com/contact/',
          'https://www.gentlegiantstudios.com/secure-file-upload/',
        ],
      },
    ],
    jobs: [
      {
        label: '01',
        title: 'A part today or tomorrow',
        shops: 'eCadCam, Hollywood 3D Printing',
        body: 'eCadCam lists same-day FDM and wax parts uploaded before noon and picked up in Glendale. Hollywood 3D Printing delivers finished work same day across the city. PrintAWorld can’t do same-day or next-day in Los Angeles.',
      },
      {
        label: '02',
        title: 'Film and TV props',
        shops: 'There You Have It, Gentle Giant Studios, Hollywood 3D Printing',
        body: 'There You Have It and Gentle Giant Studios in Burbank build props, maquettes and collectibles for film, TV and theme parks. Hollywood 3D Printing makes props for prop masters, art directors and production designers.',
      },
      {
        label: '03',
        title: 'Very large builds and architectural models',
        shops: 'PrintAWorld, Hollywood 3D Printing, There You Have It',
        body: 'We print builds of 6 ft and more in sections, and architectural and scale models of buildings, sites and interiors for presentations, competitions and displays. Hollywood 3D Printing lists pieces up to 10–15 ft with assembly; There You Have It lists SLA up to 1000 mm.',
      },
      {
        label: '04',
        title: 'Production runs and large orders',
        shops: 'PrintAWorld, eCadCam',
        body: 'Our in-house FDM print farm takes large production orders, and a person reviews the job before it runs. eCadCam works from a single prototype to full production, with an instant online quote.',
      },
      {
        label: '05',
        title: 'Painted, plated and finished prints',
        shops: 'PrintAWorld, Hollywood 3D Printing, Gentle Giant Studios',
        body: 'We sand, prime, paint and metal-plate prints so they arrive ready to show. Hollywood 3D Printing lists Pantone-matched painting and chrome or gold electroplating; Gentle Giant Studios lists professional painting.',
      },
      {
        label: '06',
        title: 'Stainless steel and metal parts',
        shops: 'PrintAWorld, eCadCam',
        body: 'We print solid stainless steel with SLM in our own studio. eCadCam lists DMLS in 316L stainless and titanium, quoted by an engineer.',
      },
    ],
    otherShopFits: [
      [
        'eCadCam',
        'You want an instant online price, or a small part the same day to collect in Glendale.',
      ],
      [
        'Hollywood 3D Printing',
        'You want same-day delivery across Los Angeles, or an activation or trade show piece from a team you can meet locally.',
      ],
      [
        'There You Have It',
        'You are a prop master or production that wants an LA prop shop with very large SLA printers.',
      ],
      [
        'Gentle Giant Studios',
        'You need a film maquette, a collectible or a theme park piece from a studio that has worked with LA film studios for over 20 years.',
      ],
    ],
    versus: [
      'Choose a local Los Angeles shop when you need to see the piece, pick it up, or have it today. eCadCam lists same-day FDM parts picked up in Glendale, Hollywood 3D Printing delivers same day across the city, and the Burbank prop shops work with film crews in person.',
      `Choose PrintAWorld when you want one team to own the result and a few days is fine: a production run from our FDM print farm, an architectural model, a build bigger than one printer, a painted or plated piece, or a stainless steel part. We have many clients in Los Angeles, reply quickly and get projects done in a matter of days, with no setup fee and prints from about $${pricing.printFrom}. We can’t do same-day or next-day in LA.`,
    ],
    faqs: [
      {
        question: `What are the best 3D printing services for Los Angeles in ${year}?`,
        answer:
          'It depends on the job. eCadCam in Glendale suits quick parts with an instant online price. Hollywood 3D Printing, There You Have It and Gentle Giant Studios suit film, TV and event props. PrintAWorld, a New York studio with many Los Angeles clients, suits brands, agencies, architects and production runs that need one team to own the result, in a matter of days.',
      },
      {
        question: 'Where can I get something 3D printed in Los Angeles?',
        answer: `Local shops include eCadCam (Glendale), Hollywood 3D Printing (Van Nuys), There You Have It (Burbank, by appointment) and Gentle Giant Studios (Burbank). PrintAWorld prints in New York and ships to Los Angeles, from about $${pricing.printFrom} with a one-print minimum and no setup fee.`,
      },
      {
        question: 'Can I get something 3D printed the same day in Los Angeles?',
        answer:
          'For small parts, sometimes. eCadCam lists same-day FDM and castable wax parts uploaded before 12:00 PT and picked up in Glendale, and Hollywood 3D Printing offers same-day delivery of finished work across the city. PrintAWorld has no location in Los Angeles, so same-day or next-day isn’t possible there; we get projects done in a matter of days.',
      },
      {
        question: 'Who makes 3D printed props for film and TV in Los Angeles?',
        answer:
          'There You Have It in Burbank calls itself one of Hollywood’s leading prop manufacturing and technical service bureaus. Gentle Giant Studios in Burbank has worked with LA film studios and theme parks for over 20 years. Hollywood 3D Printing makes props for prop masters, art directors and production designers. PrintAWorld builds large, painted props and ships them to LA.',
      },
      {
        question:
          'Who prints large 3D printed parts and architectural models for Los Angeles?',
        answer:
          'PrintAWorld prints builds of 6 ft and more in sections, and architectural and scale models for presentations, competitions and displays, then ships them to LA. Hollywood 3D Printing lists pieces up to 10–15 ft with assembly, There You Have It lists SLA up to 1000 × 1000 × 580 mm, and Gentle Giant Studios prints full-size props in a single piece.',
      },
      {
        question: 'Who does metal 3D printing for Los Angeles?',
        answer:
          'PrintAWorld prints solid stainless steel parts with SLM in its New York studio and ships them to LA. eCadCam lists DMLS in 316L stainless and titanium, quoted by an engineer. For a metal look on a resin print, PrintAWorld, Hollywood 3D Printing and There You Have It list metal plating.',
      },
      {
        question: 'How much does 3D printing cost in Los Angeles?',
        answer: `It depends on size, material, quantity and finish. PrintAWorld prints start from about $${pricing.printFrom} with no setup fee. eCadCam shows an instant online price with no minimum order, and Hollywood 3D Printing gives instant quotes once you create an account. The other shops quote per project.`,
      },
    ],
  },
})

// Houston shops, read 2026-11-03. Cosine Additive was left out: nothing on
// its visible pages is newer than 2023. 3dprotohouse is named by town only;
// its listed street address looks like a home.
const houston = cityGuide({
  city: 'Houston',
  slug: 'houston',
  cityPage: '/3d-printing-service-houston/',
  hero: 'chrome',
  local: {
    answer:
      'The best 3D printing service for Houston depends on the job. For engineering and production parts with an instant price, i-SOLIDS. For oil and gas parts in runs of 10 to 1,000, Spline Arc. For brands, agencies, architects and production work that need one team to own the result, PrintAWorld ships from New York in days.',
    shops: [
      {
        id: 'i-solids',
        name: 'i-SOLIDS',
        url: 'https://www.i-solids.com/',
        where:
          'North Houston (21602 E Hardy Road). Local pickup on request; FedEx shipping, overnight on request.',
        prints:
          'Over 150 printers: desktop and industrial FDM (including PEEK and Ultem 9085), HP MJF, SLS and SLA. Polishing, vapor smoothing, painting and Cerakote.',
        price:
          'Instant online quote; a person reviews each order before payment.',
        goodFor:
          'Engineering parts from prototyping to large volume production. Orders paid after noon are scheduled the next business day.',
      },
      {
        id: 'spline-arc',
        name: 'Spline Arc',
        url: 'https://www.splinearc.com/',
        where:
          'Houston; its site lists no street address. Pickup or local delivery for business accounts; ships to all 50 states in 1–3 business days.',
        prints:
          'An industrial FDM print farm in engineering plastics (PC, nylon, carbon-fiber nylon; PEEK and ULTEM on request) and continuous carbon fiber. Light finishing and assembly.',
        price:
          'Upload form, with a fixed-price quote within 24 hours. Orders start at $150, with no setup or tooling fees.',
        goodFor:
          'Oil and gas replacement parts and tooling, aerospace, drones and government buyers, in runs of 10 to 1,000. Rush production; it says same-day delivery in Houston is real.',
      },
      {
        id: '3dprotohouse',
        name: '3dprotohouse',
        url: 'https://www.3dprotohouse.com/',
        where: 'Humble, northeast of Houston. Ships; pickup isn’t stated.',
        prints:
          'FDM up to 245 × 245 × 260 mm and SLA up to 192 × 120 × 245 mm; its quote page also lists SLS and nylon.',
        price:
          'Instant online quote, then a final quote, or a manual quote form.',
        goodFor:
          'CAD design plus printing for prototypes, small batches and marketing merchandise. Most projects completed and shipped within a few business days.',
      },
      {
        id: 'additive-design-studio',
        name: 'Additive Design Studio',
        url: 'https://www.additivedesignstudio.com/',
        where: 'Houston; its site lists no street address.',
        prints:
          'Large-format printing up to 10 × 10 × 10 ft in polycarbonate, ABS and PLA; resin and wax for jewelry and figurines; 3D scanning. Sanding, coating and vapor smoothing.',
        price:
          'Contact form with file upload, and a quote within 48 hours. An auto-quote tool is “coming soon”.',
        goodFor:
          'Sculptural design, props, figurines and likeness sculptures, scale models, jewelry and short-run production.',
      },
    ],
    sources: [
      {
        shop: 'i-SOLIDS',
        pages: [
          'https://www.i-solids.com/',
          'https://www.i-solids.com/3dprinting-faq',
          'https://www.i-solids.com/industrial-fdm',
          'https://www.i-solids.com/sls',
        ],
      },
      {
        shop: 'Spline Arc',
        pages: [
          'https://www.splinearc.com/',
          'https://www.splinearc.com/about',
          'https://www.splinearc.com/contact',
          'https://www.splinearc.com/how-much-does-3d-printing-cost-in-houston',
        ],
      },
      {
        shop: '3dprotohouse',
        pages: [
          'https://www.3dprotohouse.com/',
          'https://www.3dprotohouse.com/faqs',
          'https://www.3dprotohouse.com/instant-3d-printing-quote',
        ],
      },
      {
        shop: 'Additive Design Studio',
        pages: [
          'https://www.additivedesignstudio.com/',
          'https://www.additivedesignstudio.com/3dprintbeast',
          'https://www.additivedesignstudio.com/pluginfo',
        ],
      },
    ],
    jobs: [
      {
        label: '01',
        title: 'Engineering and production parts',
        shops: 'i-SOLIDS, Spline Arc, PrintAWorld',
        body: 'i-SOLIDS runs over 150 printers, including MJF and SLS, with an instant online quote. Spline Arc runs an industrial FDM farm for 10 to 1,000 unit runs. Our in-house FDM print farm takes large production orders, and a person reviews each job.',
      },
      {
        label: '02',
        title: 'Oil and gas parts and tooling',
        shops: 'Spline Arc, i-SOLIDS',
        body: 'Spline Arc lists field-ready brackets, wear components and custom tooling for upstream and downstream operations. i-SOLIDS prints engineering grades such as PEEK and Ultem 9085.',
      },
      {
        label: '03',
        title: 'Very large builds and architectural models',
        shops: 'PrintAWorld, Additive Design Studio',
        body: 'We print builds of 6 ft and more in sections, and architectural and scale models of buildings, sites and interiors for presentations, competitions and displays. Additive Design Studio lists large-format prints up to 10 × 10 × 10 ft.',
      },
      {
        label: '04',
        title: 'Painted, plated and finished prints',
        shops: 'PrintAWorld, i-SOLIDS, Additive Design Studio',
        body: 'We sand, prime, paint and metal-plate prints so they arrive ready to show. i-SOLIDS lists painting and Cerakote; Additive Design Studio lists sanding, coating and vapor smoothing.',
      },
      {
        label: '05',
        title: 'Stainless steel and metal parts',
        shops: 'PrintAWorld',
        body: 'We print solid stainless steel with SLM in our own studio and ship it to Houston. None of the four Houston shops here lists metal 3D printing on its own site.',
      },
      {
        label: '06',
        title: 'A part fast in Houston',
        shops: 'Spline Arc, i-SOLIDS, 3dprotohouse',
        body: 'Spline Arc offers rush production and same-day delivery across the Houston metro. i-SOLIDS schedules orders the next business day. PrintAWorld can’t do same-day or next-day in Houston.',
      },
    ],
    otherShopFits: [
      [
        'i-SOLIDS',
        'You want an instant online price for engineering-grade parts, MJF or SLS nylon, or PEEK and Ultem, with published tolerances.',
      ],
      [
        'Spline Arc',
        'You need oil and gas or field parts, a run of 10 to 1,000 FDM parts, or same-day delivery across the Houston metro.',
      ],
      [
        '3dprotohouse',
        'You want a small prototype with CAD help from an engineer and an instant price.',
      ],
      [
        'Additive Design Studio',
        'You want a sculpture, a likeness or a prop designed and printed by a Houston studio.',
      ],
    ],
    versus: [
      'Choose a local Houston shop when you need the part today, want to pick it up, or need oil and gas parts made close to the work. Spline Arc offers same-day delivery across the Houston metro and i-SOLIDS offers local pickup; both print engineering plastics in volume.',
      `Choose PrintAWorld when you want one team to own the result and a few days is fine: a production run from our FDM print farm, an architectural model, a build bigger than one printer, a painted or plated piece, or a solid stainless steel part, which none of the Houston shops here lists. We have many clients in Houston, reply quickly and get projects done in a matter of days, with no setup fee and prints from about $${pricing.printFrom}. We can’t do same-day or next-day in Houston.`,
    ],
    faqs: [
      {
        question: `What are the best 3D printing services for Houston in ${year}?`,
        answer:
          'It depends on the job. i-SOLIDS suits engineering and volume parts with an instant price. Spline Arc suits oil and gas parts and business runs of 10 to 1,000. 3dprotohouse suits small prototypes with CAD help, and Additive Design Studio suits sculptural and large-format pieces. PrintAWorld, a New York studio with many Houston clients, suits brands, agencies, architects and production work that need one team to own the result.',
      },
      {
        question: 'Where can I get something 3D printed in Houston?',
        answer: `Local shops include i-SOLIDS (North Houston), Spline Arc (Houston), 3dprotohouse (Humble) and Additive Design Studio (Houston). PrintAWorld prints in New York and ships to Houston, from about $${pricing.printFrom} with a one-print minimum and no setup fee.`,
      },
      {
        question: 'Can I get something 3D printed the same day in Houston?',
        answer:
          'Spline Arc offers rush production and says same-day delivery across the Houston metro is real. i-SOLIDS schedules orders paid after noon for the next business day. PrintAWorld has no location in Houston, so same-day or next-day isn’t possible there; we get projects done in a matter of days.',
      },
      {
        question: 'Who 3D prints oil and gas parts in Houston?',
        answer:
          'Spline Arc lists oil and gas replacement parts and custom tooling, including field-ready brackets and wear components. i-SOLIDS prints engineering grades such as PEEK and Ultem 9085. PrintAWorld prints solid stainless steel parts with SLM and ships them to Houston.',
      },
      {
        question:
          'Who prints large 3D printed parts and architectural models for Houston?',
        answer:
          'PrintAWorld prints builds of 6 ft and more in sections, and architectural and scale models for presentations, competitions and displays, then ships them to Houston. Additive Design Studio lists large-format prints up to 10 × 10 × 10 ft.',
      },
      {
        question: 'Who does metal 3D printing for Houston?',
        answer:
          'PrintAWorld prints solid stainless steel parts with SLM in its New York studio and ships them to Houston. None of the four Houston shops on this page lists metal 3D printing on its own site. For a metal look on a resin print, PrintAWorld also offers metal plating.',
      },
      {
        question: 'How much does 3D printing cost in Houston?',
        answer: `It depends on size, material, quantity and finish. PrintAWorld prints start from about $${pricing.printFrom} with no setup fee. Spline Arc says orders start at $150, with no setup or tooling fees. i-SOLIDS and 3dprotohouse show an instant online price, and Additive Design Studio quotes within 48 hours.`,
      },
    ],
  },
})

// San Francisco and Bay Area shops, read 2026-11-03. San Francisco itself
// has two qualifying shops (Moddler has closed and points to Artifactory3d),
// so the list adds Oakland, Mountain View/San Jose and Livermore.
const sanFrancisco = cityGuide({
  city: 'San Francisco',
  slug: 'san-francisco',
  cityPage: '/3d-printing-service-san-francisco/',
  hero: 'crab',
  local: {
    area: 'in San Francisco or elsewhere in the Bay Area',
    answer:
      'The best 3D printing service for San Francisco depends on the job. For a quick part with an instant price, Simple Machining or Jinxbot. For painted models and prototypes, Artifactory3d in Oakland. For brands, agencies, architects and production work that need one team to own the result, PrintAWorld ships from New York in days.',
    shops: [
      {
        id: 'uc3-studios',
        name: 'UC3 Studios',
        url: 'https://www.uc3studio.com/',
        where:
          'Outer Richmond, San Francisco (2915 Balboa Street, per its terms). Local pickup available.',
        prints:
          'SLA and FDM in PLA, PETG, ABS/ASA, nylon and tough resin; small-batch runs of 10–500+ units.',
        price:
          'Upload STL or STEP files for a quote within 4 business hours, or use the contact form.',
        goodFor:
          'Designers, artists, educators and engineers: resin miniatures and cosplay props, STEM kits, and functional parts for hardware startups.',
      },
      {
        id: 'abc-imaging',
        name: 'ABC Imaging',
        url: 'https://www.abcimaging.com/abc-imaging-san-francisco-ca',
        where:
          'SoMa, San Francisco (832 Folsom Street), weekdays. Delivery from Marin down the peninsula to San Jose.',
        prints:
          '3D printing and scanning of models and prototypes; its site names no process. Also CAD and BIM file prep, laser cutting and CNC milling.',
        price: 'A “Get a Quote” form.',
        goodFor:
          'Architectural models, engineering design, prototypes and city planning. It also sells and leases 3D printers.',
      },
      {
        id: 'artifactory3d',
        name: 'Artifactory3d',
        url: 'https://www.artifactory3d.com/',
        where:
          'Oakland (3015 Adeline Street), by appointment only. It succeeds Moddler, which has closed.',
        prints:
          'PolyJet, FDM and SLA/DLP; build beds up to 20 × 15 × 8 in, larger parts keyed and assembled. Painting with exact color matching, vapor smoothing and assembly.',
        price: 'An inquiry form by process, plus online ordering.',
        goodFor:
          'Product designers, fine artists, architects and inventors. Standard turnaround 2–3 business days, with same-day and next-day options.',
      },
      {
        id: 'jinxbot',
        name: 'Jinxbot',
        url: 'https://jinxbot.com/',
        where:
          'Mountain View (599 Fairchild Drive) and San Jose, by appointment only; now part of Peak Fabrication Technology. Pickup by appointment.',
        prints:
          'FDM, SLA and SLS in PLA, standard and engineering resins, and Nylon 12.',
        price: 'Instant online quote: upload an STL to see the price.',
        goodFor:
          'Everyone from solo hobbyists to corporate engineering teams, from concept validation to batch runs. Typical turnaround 48–72 hours.',
      },
      {
        id: 'simple-machining',
        name: 'Simple Machining',
        url: 'https://www.simplemachining.com/',
        where:
          'Livermore (700 Terminal Circle), by appointment only. Pickup in Livermore; free ground shipping on orders over $250.',
        prints:
          'FDM, SLA, SLS and MJF in over 20 materials, including PA-11 and ULTEM 9085; FDM builds up to 36 × 36 × 36 in.',
        price:
          'Instant online quote with options by material, color and lead time. Starting at $30, with no minimum order.',
        goodFor:
          'Bay Area engineers and product teams: prototypes, jigs and fixtures. Up to 100 parts in as little as one business day.',
      },
    ],
    sources: [
      {
        shop: 'UC3 Studios',
        pages: [
          'https://www.uc3studio.com/',
          'https://www.uc3studio.com/quote',
          'https://www.uc3studio.com/industrial-prototyping',
          'https://www.uc3studio.com/terms-of-service',
        ],
      },
      {
        shop: 'ABC Imaging',
        pages: [
          'https://www.abcimaging.com/abc-imaging-san-francisco-ca',
          'https://www.abcimaging.com/3dprinting',
          'https://www.abcimaging.com/3d-quote',
        ],
      },
      {
        shop: 'Artifactory3d',
        pages: [
          'https://www.artifactory3d.com/services/3d-printing/',
          'https://www.artifactory3d.com/services/finishing-painting-and-assembly/',
          'https://www.artifactory3d.com/about/',
          'https://www.artifactory3d.com/contact/',
        ],
      },
      {
        shop: 'Jinxbot',
        pages: ['https://jinxbot.com/', 'https://jinxbot.com/faqs/'],
      },
      {
        shop: 'Simple Machining',
        pages: [
          'https://www.simplemachining.com/3d-printing-service/san-francisco-bay-area',
        ],
      },
    ],
    jobs: [
      {
        label: '01',
        title: 'A part today or tomorrow',
        shops: 'Artifactory3d, Simple Machining, Jinxbot',
        body: 'Artifactory3d offers same-day and next-day options in Oakland. Simple Machining can make up to 100 parts in as little as one business day, and Jinxbot’s typical turnaround is 48–72 hours. PrintAWorld can’t do same-day or next-day in San Francisco.',
      },
      {
        label: '02',
        title: 'Engineering parts with an instant price',
        shops: 'Simple Machining, Jinxbot, UC3 Studios',
        body: 'Simple Machining and Jinxbot show a price as soon as you upload a file. Simple Machining runs FDM, SLA, SLS and MJF; UC3 Studios quotes within 4 business hours.',
      },
      {
        label: '03',
        title: 'Very large builds and architectural models',
        shops: 'PrintAWorld, ABC Imaging, Artifactory3d',
        body: 'We print builds of 6 ft and more in sections, and architectural and scale models of buildings, sites and interiors for presentations, competitions and displays. ABC Imaging lists architectural models and city planning; Artifactory3d serves architects and assembles larger parts.',
      },
      {
        label: '04',
        title: 'Production runs and large orders',
        shops: 'PrintAWorld, UC3 Studios, Simple Machining',
        body: 'Our in-house FDM print farm takes large production orders, and a person reviews the job before it runs. UC3 Studios lists runs of 10–500+ units; Simple Machining lists up to 100 parts in as little as a day.',
      },
      {
        label: '05',
        title: 'Painted, plated and finished prints',
        shops: 'PrintAWorld, Artifactory3d',
        body: 'We sand, prime, paint and metal-plate prints so they arrive ready to show. Artifactory3d lists painting with exact color matching, vapor smoothing and assembly.',
      },
      {
        label: '06',
        title: 'Stainless steel and metal parts',
        shops: 'PrintAWorld',
        body: 'We print solid stainless steel with SLM in our own studio and ship it to San Francisco. None of the five Bay Area shops here lists metal 3D printing on its own site.',
      },
    ],
    otherShopFits: [
      [
        'UC3 Studios',
        'You want a shop in San Francisco itself with local pickup, for miniatures, cosplay props, STEM kits or a small batch.',
      ],
      [
        'ABC Imaging',
        'You want to hand CAD or BIM files to a SoMa print shop with delivery around the Bay, or you want to buy or lease a 3D printer.',
      ],
      [
        'Artifactory3d',
        'You need a painted, color-matched prototype, or a PolyJet part the same day, and can collect it in Oakland.',
      ],
      [
        'Jinxbot',
        'You want an instant price on a small FDM, SLA or SLS part and can pick it up in Mountain View or San Jose.',
      ],
      [
        'Simple Machining',
        'You need engineering parts fast, up to 100 in as little as one business day, with an instant price and pickup in Livermore.',
      ],
    ],
    versus: [
      'Choose a Bay Area shop when you need the part tomorrow or want to collect it. Artifactory3d offers same-day and next-day options in Oakland, Simple Machining can make up to 100 parts in as little as one business day, and UC3 Studios offers local pickup in San Francisco.',
      `Choose PrintAWorld when you want one team to own the result and a few days is fine: a production run from our FDM print farm, an architectural model, a build bigger than one printer, a painted or plated piece, or a solid stainless steel part, which none of the Bay Area shops here lists. We have many clients in San Francisco, reply quickly and get projects done in a matter of days, with no setup fee and prints from about $${pricing.printFrom}. We can’t do same-day or next-day in San Francisco.`,
    ],
    faqs: [
      {
        question: `What are the best 3D printing services for San Francisco in ${year}?`,
        answer:
          'It depends on the job. Simple Machining in Livermore and Jinxbot in Mountain View suit quick engineering parts with an instant price. Artifactory3d in Oakland suits painted, color-matched prototypes. UC3 Studios and ABC Imaging are in San Francisco itself. PrintAWorld, a New York studio with many San Francisco clients, suits brands, agencies, architects and production work that need one team to own the result.',
      },
      {
        question: 'Where can I get something 3D printed in San Francisco?',
        answer: `In San Francisco itself, UC3 Studios (Outer Richmond) and ABC Imaging (SoMa). Nearby: Artifactory3d (Oakland), Jinxbot (Mountain View and San Jose) and Simple Machining (Livermore). PrintAWorld prints in New York and ships to San Francisco, from about $${pricing.printFrom} with a one-print minimum and no setup fee.`,
      },
      {
        question:
          'Can I get something 3D printed the same day in San Francisco?',
        answer:
          'Sometimes, nearby. Artifactory3d in Oakland offers same-day PolyJet and next-day PolyJet or FDM, and Simple Machining lists up to 100 parts in as little as one business day. PrintAWorld has no location in San Francisco, so same-day or next-day isn’t possible there; we get projects done in a matter of days.',
      },
      {
        question: 'Who makes 3D printed architectural models in San Francisco?',
        answer:
          'PrintAWorld 3D prints architectural and scale models of buildings, sites and interiors for presentations, competitions and displays, and ships them to San Francisco. ABC Imaging in SoMa lists architectural models and city planning, and Artifactory3d in Oakland serves architects.',
      },
      {
        question: 'Who prints large 3D printed parts for San Francisco?',
        answer:
          'PrintAWorld prints builds of 6 ft and more in sections, joins and finishes them, and ships them to San Francisco. Simple Machining lists FDM builds up to 36 × 36 × 36 in, and Artifactory3d keys and assembles parts larger than its 20 × 15 × 8 in build beds.',
      },
      {
        question: 'Who does metal 3D printing for San Francisco?',
        answer:
          'PrintAWorld prints solid stainless steel parts with SLM in its New York studio and ships them to San Francisco. None of the five Bay Area shops on this page lists metal 3D printing on its own site. For a metal look on a resin print, PrintAWorld also offers metal plating.',
      },
      {
        question: 'How much does 3D printing cost in San Francisco?',
        answer: `It depends on size, material, quantity and finish. PrintAWorld prints start from about $${pricing.printFrom} with no setup fee. Simple Machining says it starts at $30 with no minimum order, and Jinxbot shows an instant price when you upload an STL. The other shops quote per project.`,
      },
    ],
  },
})

const cities = { sanFrancisco, losAngeles, houston }

module.exports = {
  cityGuide,
  cities,
  pricing,
  year,
}
